import { Timestamp, doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { withErrorHandling } from '../utils/withErrorHandling';
import { UserRole } from '../types/shared';

// Constants for collections and user statuses
export const COLLECTIONS = {
  USERS: 'users'
};

export type UserStatus = 'active' | 'inactive' | 'suspended';

// Detailed user metadata interface
export interface UserMetadata {
  phoneNumber?: string;
  timezone?: string;
  lastLogin?: Date;
  registrationSource?: string;
}

// Comprehensive Firestore user document structure
export interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  hierarchyPath: string;
  emailVerified: boolean;
  metadata?: UserMetadata;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastLoginAt?: Timestamp;
}

// Typed user interface for client-side use
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  hierarchyPath: string;
  emailVerified: boolean;
  metadata?: UserMetadata;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
}

class UsersService {
  /**
   * Convert Firestore timestamp-based user to client-side user model
   * @param fUser Firestore user document
   * @returns Converted user object
   */
  static convertFirestoreUser(fUser: FirestoreUser): User {
    return {
      ...fUser,
      createdAt: fUser.createdAt.toDate(),
      updatedAt: fUser.updatedAt.toDate(),
      lastLoginAt: fUser.lastLoginAt ? fUser.lastLoginAt.toDate() : undefined,
    };
  }

  /**
   * Retrieve a user by their unique ID
   * @param userId Firebase Auth UID
   * @returns User object or null if not found
   */
  static async getUserById(userId: string): Promise<User | null> {
    return withErrorHandling(
      async () => {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
        if (!userDoc.exists()) return null;
        
        const userData = userDoc.data() as Omit<FirestoreUser, 'id'>;
        return this.convertFirestoreUser({ ...userData, id: userDoc.id });
      },
      { 
        context: { 
          operation: 'getUserById', 
          userId 
        } 
      }
    );
  }

  /**
   * Create a new user document in Firestore
   * @param userId Firebase Auth UID
   * @param userData User creation data
   * @returns Created user ID
   */
  static async createUser(
    userId: string,
    userData: Omit<FirestoreUser, "createdAt" | "updatedAt" | "id" | "lastLoginAt">
  ): Promise<string> {
    return withErrorHandling(
      async () => {
        const now = Timestamp.now();
        const userToCreate: FirestoreUser = {
          ...userData,
          id: userId,
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          emailVerified: true,
          metadata: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...userData.metadata
          }
        };

        await setDoc(doc(db, COLLECTIONS.USERS, userId), userToCreate);
        return userId;
      },
      { 
        context: { 
          operation: 'createUser', 
          userRole: userData.role 
        } 
      }
    );
  }

  /**
   * Update an existing user document
   * @param userId Firebase Auth UID
   * @param updates Partial user update data
   */
  static async updateUser(
    userId: string, 
    updates: Partial<Omit<FirestoreUser, 'id' | 'createdAt'>>
  ): Promise<void> {
    return withErrorHandling(
      async () => {
        const userRef = doc(db, COLLECTIONS.USERS, userId);
        await updateDoc(userRef, {
          ...updates,
          updatedAt: Timestamp.now()
        });
      },
      { 
        context: { 
          operation: 'updateUser', 
          userId 
        } 
      }
    );
  }

  /**
   * Update user's last login timestamp
   * @param userId Firebase Auth UID
   */
  static async updateLastLogin(userId: string): Promise<void> {
    return withErrorHandling(
      async () => {
        const userRef = doc(db, COLLECTIONS.USERS, userId);
        await updateDoc(userRef, {
          lastLoginAt: Timestamp.now()
        });
      },
      { 
        context: { 
          operation: 'updateLastLogin', 
          userId 
        } 
      }
    );
  }

  /**
   * Soft delete a user by updating their status
   * @param userId Firebase Auth UID
   */
  static async deactivateUser(userId: string): Promise<void> {
    return withErrorHandling(
      async () => {
        const userRef = doc(db, COLLECTIONS.USERS, userId);
        await updateDoc(userRef, {
          status: 'inactive',
          updatedAt: Timestamp.now()
        });
      },
      { 
        context: { 
          operation: 'deactivateUser', 
          userId 
        } 
      }
    );
  }
}

export default UsersService;
